/* ============================================================
   The action sheets a row opens — the mobile TaskActionSheet and
   HabitActionSheet, as compact dialogs: the name (with a quiet delete at the
   far right), reschedule chips, a row of secondary actions, and the dominant
   primary at the bottom. They read the LIVE row from the store so a
   multi-target habit updates while the sheet stays open.
   ============================================================ */
import React, { useState } from 'react';
import { addDaysStr, formatTime, type ReplanWhere, todayStr, useStore } from '@habishine/client-core';

import { useEditors } from '../shell/Editors';
import { hueVar } from '../theme/hues';
import { Dialog, inputClass } from './Dialog';
import { Icon, type IconName } from './Icon';
import { HabitRing, Ring } from './Ring';
import { Button } from './ui';

function Secondary({ icon, label, onClick, active }: { icon: IconName; label: string; onClick: () => void; active?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={`flex flex-1 flex-col items-center gap-1.5 rounded-[14px] py-3 text-[12px] font-semibold transition-[background-color,transform] duration-150 active:scale-[0.97] ${active ? 'bg-frog-soft text-frog' : 'bg-card-sunk text-ink-2 hover:bg-hairline hover:text-ink'}`}>
      <Icon name={icon} size={18} strokeWidth={2.2} filled={active && icon === 'pin'} />
      {label}
    </button>
  );
}

export function TaskSheet({ taskId, day, onClose }: { taskId: string; day: string; onClose: () => void }) {
  const store = useStore();
  const { editTask } = useEditors();
  const task = store.state.tasks.find((t) => t.id === taskId);
  const [picking, setPicking] = useState(false);
  if (!task) return null;
  const hue = store.hueForTask(task);
  const pinned = store.pinnedIdFor(day) === task.id;
  const run = (fn: () => void) => { fn(); onClose(); };
  const chips: { label: string; where: ReplanWhere }[] = [
    { label: 'Today', where: 'Today' }, { label: 'Tomorrow', where: 'Tomorrow' }, { label: 'This weekend', where: 'This weekend' }, { label: 'Someday', where: 'Someday' },
  ];
  const title = (
    <div className="flex items-start gap-3">
      <Ring size={34} stroke={4} hue={hue} pct={task.done ? 1 : 0} check />
      <div className="min-w-0 flex-1">
        <div className="font-display text-[22px] font-semibold leading-[1.15] text-ink">{task.name}</div>
        <div className="text-[13px] text-meta">
          {task.done ? 'Done' : task.skipped ? 'Skipped — not doing this' : task.scheduledFor ? `${task.scheduledFor === todayStr() ? 'Today' : task.scheduledFor}${task.remindAt != null ? ` · ${formatTime(task.remindAt)}` : ''}` : 'In the backlog'}
        </div>
      </div>
      <button type="button" aria-label={`Delete ${task.name}`} onClick={() => run(() => store.deleteTask(task.id))} className="rounded-full p-1.5 text-tertiary hover:bg-nudge hover:text-replan">
        <Icon name="trash" size={16} />
      </button>
    </div>
  );
  return (
    <Dialog title="" onClose={onClose}>
      {title}
      {task.done ? (
        <Button variant="primary" className="w-full" onClick={() => run(() => store.undoDone(task.id))}>Bring back</Button>
      ) : task.skipped ? (
        <Button variant="primary" className="w-full" onClick={() => run(() => store.unskipTask(task.id))}>Un-skip</Button>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {chips.map((c) => (
              <button key={c.where} type="button" onClick={() => run(() => store.replanTo(task.id, c.where))} className="rounded-full bg-card-sunk px-3.5 py-1.5 text-[13px] font-semibold text-ink-2 transition hover:bg-hairline hover:text-ink">
                {c.label}
              </button>
            ))}
            <button type="button" onClick={() => setPicking((p) => !p)} className="rounded-full bg-card-sunk px-3.5 py-1.5 text-[13px] font-semibold text-ink-2 transition hover:bg-hairline hover:text-ink">Pick a day…</button>
          </div>
          {picking ? (
            <input type="date" className={inputClass} defaultValue={task.scheduledFor ?? addDaysStr(todayStr(), 1)} onChange={(e) => e.target.value && run(() => store.rescheduleTask(task.id, e.target.value))} autoFocus />
          ) : null}
          <div className="flex gap-2">
            <Secondary icon="edit" label="Edit" onClick={() => editTask(task)} />
            <Secondary icon="pin" label={pinned ? 'Unpin' : 'Pin'} active={pinned} onClick={() => run(() => store.togglePin(task.id, day))} />
            <Secondary icon="skip-forward" label="Skip" onClick={() => run(() => store.skipTask(task.id))} />
          </div>
          <Button variant="primary" className="w-full" onClick={() => run(() => store.completeItem(task.id))}>Mark done</Button>
        </>
      )}
    </Dialog>
  );
}

export function HabitSheet({ habitId, day, onClose }: { habitId: string; day: string; onClose: () => void }) {
  const store = useStore();
  const { editHabit } = useEditors();
  const habit = store.habitsForDay(day).find((h) => h.id === habitId);
  if (!habit) return null;
  const pinned = store.pinnedIdFor(day) === habit.id;
  const multi = habit.target > 1;
  const finishing = !multi || habit.progress + 1 >= habit.target;
  const pct = multi ? habit.progress / habit.target : habit.done ? 1 : 0;
  const note = habit.done ? 'Done today' : multi ? `${habit.progress} of ${habit.target} today` : habit.period ? `${habit.period.done} of ${habit.period.target} this week` : habit.streak > 0 ? `🔥 ${habit.streak}-${habit.unit} streak` : 'Not yet today';
  const run = (fn: () => void) => { fn(); onClose(); };
  return (
    <Dialog title="" onClose={onClose}>
      <div className="flex items-center gap-3">
        <HabitRing hue={habit.hue} pct={pct} emoji={habit.emoji} done={habit.done} size={52} />
        <div className="min-w-0 flex-1">
          <div className="font-display text-[22px] font-semibold leading-[1.15] text-ink">{habit.name}</div>
          <div className="text-[13px] text-meta">{note}</div>
        </div>
      </div>
      <div className="flex gap-2">
        <Secondary icon="edit" label="Edit" onClick={() => editHabit(habit)} />
        <Secondary icon="pin" label={pinned ? 'Unpin' : 'Pin'} active={pinned} onClick={() => run(() => store.togglePin(habit.id, day))} />
      </div>
      {habit.done ? (
        <Button variant="primary" className="w-full" onClick={() => run(() => store.completeItem(habit.id, day))}>Undo</Button>
      ) : (
        <Button
          variant="primary"
          className="w-full"
          style={{ background: hueVar(habit.hue, 'k') }}
          onClick={() => (finishing ? run(() => store.completeItem(habit.id, day)) : store.completeItem(habit.id, day))}
        >
          {finishing ? 'Mark done' : `Log one (${habit.progress + 1} of ${habit.target})`}
        </Button>
      )}
    </Dialog>
  );
}
